import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Toaster } from './ui/sonner';
import { fetchGeolocation, submitLead, prepareLeadPayload } from '../services/apiService';

const countryCodes = [
  { code: '+1', country: 'US/Canada' },
  { code: '+44', country: 'UK' },
  { code: '+91', country: 'India' },
  { code: '+86', country: 'China' },
  { code: '+49', country: 'Germany' },
  { code: '+33', country: 'France' },
  { code: '+81', country: 'Japan' },
  { code: '+61', country: 'Australia' },
  { code: '+92', country: 'Pakistan' },
  { code: '+31', country: 'Netherlands' },
];

const LeadFormPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [geoData, setGeoData] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    country_code: '+1',
    contact_number: '',
    message: ''
  });

  useEffect(() => {
    const handleOpenLeadForm = () => {
      setIsOpen(true);
    };

    window.addEventListener('openLeadForm', handleOpenLeadForm);
    return () => window.removeEventListener('openLeadForm', handleOpenLeadForm);
  }, []);

  // Fetch geolocation data when form opens
  useEffect(() => {
    if (isOpen && !geoData) {
      fetchGeolocation().then(data => {
        if (data) {
          setGeoData(data);
          // Set country code based on geolocation if available
          if (data.data?.countryCode) {
            const countryCodeMap = {
              'US': '+1',
              'UK': '+44',
              'IN': '+91',
              'CN': '+86',
              'DE': '+49',
              'FR': '+33',
              'JP': '+81',
              'AU': '+61',
              'PK': '+92',
              'NL': '+31'
            };
            const dialCode = countryCodeMap[data.data.countryCode] || '+1';
            setFormData(prev => ({ ...prev, country_code: dialCode }));
          }
        }
      });
    }
  }, [isOpen, geoData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate contact number
    if (formData.contact_number.length > 10) {
      toast.error('Contact number must be maximum 10 digits');
      return;
    }
    
    if (!/^\d+$/.test(formData.contact_number)) {
      toast.error('Contact number must contain only numbers');
      return;
    }

    setLoading(true);
    
    try {
      // Prepare payload
      const payload = prepareLeadPayload(formData, geoData);
      
      // Submit lead
      const response = await submitLead(payload);
      
      // Handle response based on status code
      if (response.statusCode === 200 && response.data?.data?.customerId) {
        // Success - redirect to thanks page
        toast.success('Thank you! Redirecting...');
        
        // Clear form
        setFormData({
          full_name: '',
          email: '',
          country_code: '+1',
          contact_number: '',
          message: ''
        });
        
        // Close popup
        setIsOpen(false);
        
        // Redirect to thanks page
        setTimeout(() => {
          window.location.href = '/thanks';
        }, 1500);
        
      } else if (response.statusCode === 401) {
        // Unauthorized error
        toast.error(response.data?.message || 'Unauthorized request');
      } else if (response.statusCode === 400) {
        // Bad request - show specific error
        toast.error(response.data?.errorMessage || 'Invalid data submitted');
      } else {
        // Other errors
        toast.error(response.message || 'Failed to submit. Please try again.');
      }
      
    } catch (error) {
      toast.error('An unexpected error occurred. Please try again.');
      console.error('Lead submission error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // For contact number, only allow numbers and max 10 digits
    if (name === 'contact_number') {
      const numericValue = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, [name]: numericValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  return (
    <>
      <Toaster position="top-center" richColors />
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
            data-testid="lead-form-overlay"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white w-full max-w-lg shadow-2xl relative max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="lead-form-modal"
            >
              {/* Close Button */}
              <button
                onClick={() => setIsOpen(false)}
                className="absolute top-2 right-2 md:top-4 md:right-4 text-slate-400 hover:text-slate-900 transition-colors z-10"
                data-testid="lead-form-close-button"
              >
                <X className="h-5 w-5 md:h-6 md:w-6" />
              </button>

              {/* Form Header */}
              <div className="border-b border-slate-100 px-4 md:px-8 pt-4 md:pt-6 pb-3 md:pb-4">
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-1 md:mb-2">Get Legal Help</h2>
                <p className="text-sm md:text-base text-slate-600">Fill out the form and we'll contact you within 24 hours.</p>
                {geoData?.data?.countryName && (
                  <p className="text-xs md:text-sm text-amber-700 mt-1 md:mt-2">
                    📍 Detected location: {geoData.data.countryName}
                  </p>
                )}
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-3">
                {/* Full Name */}
                <div>
                  <label htmlFor="full_name" className="block text-sm font-medium text-slate-700 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="full_name"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 md:px-4 md:py-3 text-sm md:text-base border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none transition-colors"
                    placeholder="John Doe"
                    data-testid="lead-form-full-name"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 md:px-4 md:py-3 text-sm md:text-base border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none transition-colors"
                    placeholder="john@example.com"
                    data-testid="lead-form-email"
                  />
                </div>

                {/* Contact Number */}
                <div>
                  <label htmlFor="contact_number" className="block text-sm font-medium text-slate-700 mb-1">
                    Contact Number *
                  </label>
                  <div className="flex gap-2">
                    <select
                      name="country_code"
                      value={formData.country_code}
                      onChange={handleChange}
                      className="px-2 py-2 md:px-4 md:py-3 text-sm md:text-base border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none transition-colors"
                      data-testid="lead-form-country-code"
                    >
                      {countryCodes.map((item) => (
                        <option key={item.code} value={item.code}>
                          {item.code}
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      id="contact_number"
                      name="contact_number"
                      value={formData.contact_number}
                      onChange={handleChange}
                      required
                      maxLength="10"
                      className="flex-1 px-3 py-2 md:px-4 md:py-3 text-sm md:text-base border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none transition-colors"
                      placeholder="1234567890"
                      data-testid="lead-form-contact-number"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Maximum 10 digits, numbers only</p>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-1">
                    Message / Legal Inquiry
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="3"
                    className="w-full px-3 py-2 md:px-4 md:py-3 text-sm md:text-base border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none transition-colors resize-none"
                    placeholder="Describe your legal needs..."
                    data-testid="lead-form-message"
                  ></textarea>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-slate-900 text-white hover:bg-slate-800 px-6 py-3 md:px-8 md:py-4 text-base md:text-lg font-serif transition-all hover:tracking-wider shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="lead-form-submit-button"
                >
                  {loading ? 'Submitting...' : 'Submit Inquiry'}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default LeadFormPopup;
